import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import createGlobe, { Marker } from 'cobe';
import './Globe.scss';

const GLOBE_CONFIG: any = {
    width: 800,
    height: 800,
    onRender: () => { },
    devicePixelRatio: 2,
    phi: 0,
    theta: 0.3,
    dark: 0,
    diffuse: 0.4,
    mapSamples: 16000,
    mapBrightness: 1.2,
    baseColor: [1, 1, 1],
    markerColor: [251 / 255, 100 / 255, 21 / 255],
    glowColor: [1, 1, 1],
    markers: [
        { location: [30.3165, 78.0322], size: 0.09 },
        { location: [14.5995, 120.9842], size: 0.03 }
    ],
};

interface GlobeProps {
    className?: string;
    config?: any;
    userLocation?: [number, number] | null;
}

export function Globe({ className = '', config = GLOBE_CONFIG, userLocation = null }: GlobeProps) {
    const phiRef = useRef(0);
    // Drag rotation is a ref, not state — otherwise every mouse move re-renders
    // this component, recreates onRender, and forces the WebGL globe to rebuild.
    const rRef = useRef(0);
    const widthRef = useRef(0);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const pointerInteracting = useRef<number | null>(null);
    const pointerInteractionMovement = useRef(0);

    // Assemble the marker list. NOTE: cobe has a bug where runtime marker
    // updates (via state.markers in onRender) set the marker-count uniform
    // to `length` instead of `length * 2` — the shader packs each marker as
    // two vec4s, so only ceil(length/2) actually render. Init path sets the
    // count correctly, so we rebuild the globe when the marker set changes
    // (throttled upstream in GlobeDemo) rather than mutating markers per-frame.
    const markers = useMemo<Marker[]>(() => {
        const base: Marker[] = config.markers || [];
        return userLocation
            ? [...base, { location: userLocation, size: 0.1, color: [0.231, 0.51, 0.965] }] // blue "you are here" pin
            : base;
    }, [config, userLocation]);

    const updatePointerInteraction = (value: number | null) => {
        pointerInteracting.current = value;
        if (canvasRef.current) {
            canvasRef.current.style.cursor = value !== null ? "grabbing" : "grab";
        }
    };

    const updateMovement = (clientX: number) => {
        if (pointerInteracting.current !== null) {
            const delta = clientX - pointerInteracting.current;
            pointerInteractionMovement.current = delta;
            rRef.current = delta / 200;
        }
    };

    // Stable across renders — reads rotation from refs, no state deps.
    const onRender = useCallback((state: any) => {
        if (!pointerInteracting.current) phiRef.current += 0.005;
        state.phi = phiRef.current + rRef.current;
        state.width = widthRef.current * 2;
        state.height = widthRef.current * 2;
    }, []);

    const onResize = useCallback(() => {
        if (canvasRef.current) {
            widthRef.current = canvasRef.current.offsetWidth;
        }
    }, []);

    useEffect(() => {
        window.addEventListener("resize", onResize);
        onResize();

        if (!canvasRef.current) return;

        const globe = createGlobe(canvasRef.current, {
            ...config,
            width: widthRef.current * 2,
            height: widthRef.current * 2,
            markers,
            onRender,
        });

        setTimeout(() => {
            if (canvasRef.current) canvasRef.current.style.opacity = "1";
        });
        return () => {
            window.removeEventListener("resize", onResize);
            globe.destroy();
        };
        // `markers` identity only changes when userLocation crosses the throttle
        // threshold, so rebuilds are infrequent — see GlobeDemo.startWatching.
    }, [config, markers, onRender, onResize]);

    return (
        <div className={`globe-container ${className}`}>
            <canvas
                className="globe-canvas"
                ref={canvasRef}
                onPointerDown={(e) =>
                    updatePointerInteraction(
                        e.clientX - pointerInteractionMovement.current
                    )
                }
                onPointerUp={() => updatePointerInteraction(null)}
                onPointerOut={() => updatePointerInteraction(null)}
                onMouseMove={(e) => updateMovement(e.clientX)}
                onTouchMove={(e) =>
                    e.touches[0] && updateMovement(e.touches[0].clientX)
                }
            />
        </div>
    );
}
